"use client";

import React, { useEffect, useRef } from "react";
import { useAppSelector } from "@/lib/store/hooks";

const PageVisitLogger: React.FC = () => {
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(0);
  const entityId = useAppSelector((state) => state.profile.entityId);

  // Record start time on mount
  useEffect(() => {
    startTimeRef.current = performance.now();
  }, []);

  // Send page visit log when entityId becomes available
  useEffect(() => {
    if (!entityId) return;
    const navEntries = performance.getEntriesByType("navigation");
    const navTiming = navEntries[0] as PerformanceNavigationTiming | undefined;
    const buildTimeMs = navTiming ? navTiming.responseStart : 0;
    const loadTimeMs = navTiming ? navTiming.loadEventEnd : 0;
    const pagePath = window.location.pathname;
    const browser = navigator.userAgent;

    const sendLog = async () => {
      let ip = "";
      try {
        const ipRes = await fetch("/next/api/ip");
        const ipData = await ipRes.json();
        ip = ipData.ip || "";
      } catch {
        // ignore
      }

      try {
        const res = await fetch("/next/api/page-visits", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            entityId,
            pagePath,
            browser,
            ip,
            buildTimeMs,
            loadTimeMs,
          }),
        });
        const data = await res.json();
        if (data.sessionId) {
          sessionIdRef.current = data.sessionId;
        }
      } catch (err) {
        console.error("Error logging page visit:", err);
      }
    };
    sendLog();
  }, [entityId]);

  // On unload, send time spent
  useEffect(() => {
    const handleUnload = () => {
      const endTime = performance.now();
      const timeSpentMs = Math.round(endTime - startTimeRef.current);
      if (sessionIdRef.current) {
        const payload = JSON.stringify({
          sessionId: sessionIdRef.current,
          timeSpentMs,
        });
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/next/api/page-visits", payload);
        } else {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", "/next/api/page-visits", false);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.send(payload);
        }
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  return null;
};

export default PageVisitLogger;
